import { z } from 'zod';
import { handoverClosingSchema } from './handover-closing.js';
import { catalogEntrySchema, catalogContactSchema } from './catalog.js';
import { generalCheckResultInputSchema, GENERAL_CHECK_LIMITS, handoverGeneralChecksSchema, } from './general-checks.js';
import { CONTRACTOR_SOURCES, FINDING_STATUSES, INSPECTION_LIMITS, handoverInspectionSchema, inspectionResultInputSchema, } from './inspection.js';
// Apartment handovers, H-1: the draft protocol shared by both handover types. INITIAL (מסירה
// ראשונית) and FINAL (מסירה סופית) use one data model and one screen; the type is a field.
// Signatures, declarations, official documents and photos are later milestones.
export const HANDOVER_TYPES = ['initial', 'final'];
export const HANDOVER_STATUSES = ['draft', 'discarded', 'completed', 'refused'];
export const HANDOVER_TYPE_LABELS = {
    initial: 'מסירה ראשונית',
    final: 'מסירה סופית',
};
export const HANDOVER_STATUS_LABELS = {
    draft: 'טיוטה',
    discarded: 'טיוטה שנמחקה',
    completed: 'הושלמה',
    refused: 'לא הושלמה – סירוב לחתימה',
};
/** Limits shared by the form, the API and the database (migration 202610142000). */
export const HANDOVER_LIMITS = {
    area: 60,
    component: 80,
    description: 1000,
    contractor: 80,
    findings: 300,
    meterType: 40,
    meterNumber: 40,
    note: 300,
    meters: 20,
    photosPerFinding: 20,
    resolutionNote: INSPECTION_LIMITS.resolutionNote,
    reinspectionNote: 1000,
    reinspections: 300,
    generalChecks: GENERAL_CHECK_LIMITS.items,
    inspectionItems: INSPECTION_LIMITS.templateItems,
    /** Bytes of one uploaded photo (the browser resizes before uploading). */
    photoBytes: 10 * 1024 * 1024,
};
/** Formats the server stores; the browser converts other images to JPEG before uploading. */
export const PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png'];
/** A meter reading: up to 11 integer digits and 3 decimals, sent as text so nothing is rounded. */
export const READING_PATTERN = /^[0-9]{1,11}(\.[0-9]{1,3})?$/;
const isoDate = z.iso.date();
const requiredText = (max) => z.string().trim().min(1).max(max);
/** Optional text: empty (after trimming) is stored as no value. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => (value === '' ? null : value));
// ---- Responses -------------------------------------------------------------------------------
/** A parking space or storage room of the apartment, as Master Data holds it now. */
export const handoverSpaceSchema = z
    .object({
    number: z.string(),
    /** The level label (for example "-1"); empty when the space has no level. */
    location: z.string(),
    structureCode: z.string(),
})
    .strict();
/** The main property and its secondary properties, read from Master Data (never re-entered). */
export const handoverApartmentSchema = z
    .object({
    id: z.uuid(),
    building: z.string(),
    floor: z.string(),
    apartmentNumber: z.string(),
    modelType: z.string().nullable(),
    parking: z.array(handoverSpaceSchema),
    storage: z.array(handoverSpaceSchema),
})
    .strict();
export const handoverSummarySchema = z
    .object({
    id: z.uuid(),
    type: z.enum(HANDOVER_TYPES),
    status: z.enum(HANDOVER_STATUSES),
    protocolNumber: z.string(),
    handoverDate: isoDate.nullable(),
    findingCount: z.number().int().min(0),
    /**
     * Inspection progress (H-Checklist), checklist items and general checks together; null before
     * the inspection starts.
     */
    progress: z
        .object({
        total: z.number().int().min(0),
        checked: z.number().int().min(0),
        openFindings: z.number().int().min(0),
        /** Final handover: initial findings not reinspected yet (H-Next). */
        awaitingReinspection: z.number().int().min(0),
    })
        .strict()
        .nullable(),
    updatedAt: z.iso.datetime({ offset: true }),
})
    .strict();
const handoverProjectSchema = z
    .object({ id: z.uuid(), code: z.string(), name: z.string() })
    .strict();
const handoverPermissionsSchema = z.object({ create: z.boolean(), edit: z.boolean() }).strict();
/** `GET /projects/:id/handover-apartments`: every apartment with its handovers. */
export const handoverApartmentListResponseSchema = z
    .object({
    project: handoverProjectSchema,
    permissions: handoverPermissionsSchema,
    items: z.array(z
        .object({
        apartment: handoverApartmentSchema,
        handovers: z.array(handoverSummarySchema),
    })
        .strict()),
})
    .strict();
export const handoverPhotoSchema = z
    .object({
    id: z.uuid(),
    contentType: z.enum(PHOTO_CONTENT_TYPES),
    width: z.number().int().min(1),
    height: z.number().int().min(1),
    byteSize: z.number().int().min(1),
})
    .strict();
// ---- Reinspection (final handover) ---------------------------------------------------------------
export const REINSPECTION_RESULTS = ['pending', 'repaired', 'still_defective'];
export const REINSPECTION_RESULT_LABELS = {
    pending: 'טרם נבדק מחדש',
    repaired: 'תוקן',
    still_defective: 'עדיין לא תקין',
};
/** An initial handover, as the final handover refers to it. */
export const handoverRefSchema = z
    .object({
    id: z.uuid(),
    protocolNumber: z.string(),
    handoverDate: isoDate.nullable(),
})
    .strict();
/** The initial finding, as recorded in the initial handover (read-only here). */
export const initialFindingSchema = z
    .object({
    id: z.uuid(),
    area: z.string(),
    component: z.string(),
    workCategory: z.string().nullable(),
    description: z.string(),
    inspectedOn: isoDate,
    contractor: z.string().nullable(),
    /** Its state in the initial handover. */
    status: z.enum(FINDING_STATUSES),
    /** Photos of the initial handover (read through the initial handover). */
    photos: z.array(handoverPhotoSchema),
})
    .strict();
export const reinspectionSchema = z
    .object({
    finding: initialFindingSchema,
    /** null: nothing recorded yet ("טרם נבדק מחדש"). */
    id: z.uuid().nullable(),
    result: z.enum(REINSPECTION_RESULTS),
    inspectedOn: isoDate.nullable(),
    note: z.string().nullable(),
    /** New photos, stored with the final handover. */
    photos: z.array(handoverPhotoSchema),
})
    .strict();
/** The reinspection of a final handover; null for an initial handover. */
export const handoverReinspectionSchema = z
    .object({
    /** The linked initial handover; null when the final handover has no link (yet). */
    initial: handoverRefSchema.nullable(),
    /** Without a link: the apartment's initial handover that can be linked, if any. */
    linkable: handoverRefSchema.nullable(),
    /** The initial findings still open, and those reinspected here (whatever their state now). */
    items: z.array(reinspectionSchema),
})
    .strict();
export const reinspectionInputSchema = z
    .object({
    /** The initial finding reinspected. */
    findingId: z.uuid(),
    result: z.enum(REINSPECTION_RESULTS),
    inspectedOn: isoDate.nullable(),
    note: optionalText(HANDOVER_LIMITS.reinspectionNote),
    /** New photos of the final handover, in display order. */
    photoIds: z.array(z.uuid()).max(HANDOVER_LIMITS.photosPerFinding),
})
    .strict();
/** `POST /projects/:id/handovers/:handoverId/initial-link`: links an unlinked final handover. */
export const initialLinkRequestSchema = z
    .object({ rowVersion: z.number().int().min(1), initialHandoverId: z.uuid() })
    .strict();
/**
 * A finding. The texts are the values recorded on the finding when its catalog entries were chosen
 * (they do not change when an entry is renamed); the ids reference the catalog entries, and are
 * null for findings recorded before the catalogs existed.
 */
export const handoverFindingSchema = z
    .object({
    id: z.uuid(),
    area: z.string(),
    areaId: z.uuid().nullable(),
    component: z.string(),
    componentId: z.uuid().nullable(),
    description: z.string(),
    inspectedOn: isoDate,
    contractor: z.string().nullable(),
    contractorId: z.uuid().nullable(),
    /** The work category (תחום עבודה) recorded on the finding, if any. */
    workCategory: z.string().nullable(),
    workCategoryId: z.uuid().nullable(),
    /** The contractor's current contact, only with `projects.supplier.view`. */
    contractorContact: catalogContactSchema.nullable(),
    /**
     * The contact as it was when the contractor was chosen (findings saved since H-Checklist), only
     * with `projects.supplier.view`.
     */
    contractorContactRecorded: catalogContactSchema.nullable(),
    /** default: the work category's default contractor was kept; manual: chosen by the user. */
    contractorSource: z.enum(CONTRACTOR_SOURCES).nullable(),
    /** The inspection item the finding was recorded on (H-Checklist); null for free findings. */
    inspectionItemId: z.uuid().nullable(),
    status: z.enum(FINDING_STATUSES),
    resolutionNote: z.string().nullable(),
    photos: z.array(handoverPhotoSchema),
})
    .strict();
export const meterReadingSchema = z
    .object({
    id: z.uuid(),
    meterType: z.string(),
    meterNumber: z.string().nullable(),
    reading: z.string().nullable(),
    note: z.string().nullable(),
})
    .strict();
/** `GET /projects/:id/handovers/:handoverId`: the whole draft protocol. */
/** Why a draft was discarded. */
export const DISCARD_REASONS = ['opened_by_mistake', 'wrong_apartment', 'other'];
export const DISCARD_REASON_LABELS = {
    opened_by_mistake: 'נפתחה בטעות',
    wrong_apartment: 'נפתחה לדירה שגויה',
    other: 'סיבה אחרת',
};
export const handoverDocumentSchema = z
    .object({
    id: z.uuid(),
    type: z.enum(HANDOVER_TYPES),
    status: z.enum(HANDOVER_STATUSES),
    protocolNumber: z.string(),
    handoverDate: isoDate.nullable(),
    rowVersion: z.number().int().min(1),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
    project: handoverProjectSchema,
    apartment: handoverApartmentSchema,
    findings: z.array(handoverFindingSchema),
    meters: z.array(meterReadingSchema),
    inspection: handoverInspectionSchema,
    /** General handover checks (H-Next). */
    generalChecks: handoverGeneralChecksSchema,
    /** The reinspection of the initial findings (final handovers; null for initial ones). */
    reinspection: handoverReinspectionSchema.nullable(),
    /** The active appointment of this apartment and type, with its assigned staff (SCHEDULE). */
    appointment: z
        .object({
        startsAt: z.iso.datetime({ offset: true }),
        durationMinutes: z.number().int(),
        status: z.enum(['planned', 'confirmed', 'completed', 'cancelled']),
        primary: z.string().nullable(),
        participants: z.array(z.string()),
    })
        .strict()
        .nullable(),
    /** A discarded draft: when and why (the record and its history are kept). */
    discarded: z
        .object({ at: z.iso.datetime({ offset: true }), reason: z.enum(DISCARD_REASONS) })
        .strict()
        .nullable(),
    /** Signatures, the acknowledgment wording, what prevents completion, and the closing. */
    closing: handoverClosingSchema,
    /**
     * `discard`: may discard this draft (`handover.handover.create`); `close`: may sign and close
     * it (`handover.handover.edit`). All false once the handover is not a draft.
     */
    permissions: z.object({ edit: z.boolean(), discard: z.boolean(), close: z.boolean() }).strict(),
})
    .strict();
// ---- Requests --------------------------------------------------------------------------------
export const handoverCreateRequestSchema = z
    .object({ apartmentId: z.uuid(), type: z.enum(HANDOVER_TYPES) })
    .strict();
/**
 * `POST /projects/:id/handovers/:handoverId/inspection` (checklist) and `…/general-checks` (general
 * checks): starts them on a draft opened before the template had any.
 */
export const inspectionStartRequestSchema = z
    .object({ rowVersion: z.number().int().min(1) })
    .strict();
/** `POST /projects/:id/handovers/:handoverId/discard`: discards a draft that still has `rowVersion`. */
export const handoverDiscardRequestSchema = z
    .object({
    rowVersion: z.number().int().min(1),
    reason: z.enum(DISCARD_REASONS),
    /** Required for "other"; never copied into the audit event. */
    note: z.string().trim().min(1).max(300).optional(),
})
    .strict()
    .refine((v) => v.reason !== 'other' || v.note !== undefined, {
    path: ['note'],
    message: 'required',
});
export const findingInputSchema = z
    .object({
    /** null: a new finding. Otherwise a finding of this handover. */
    id: z.uuid().nullable(),
    /**
     * Catalog entries. An unchanged reference stays valid after its entry is deactivated. null keeps
     * the recorded text of a finding saved before the catalogs existed; a new finding needs both.
     */
    areaId: z.uuid().nullable(),
    componentId: z.uuid().nullable(),
    contractorId: z.uuid().nullable(),
    /** Optional; a chosen contractor must belong to it (when both are new or changed). */
    workCategoryId: z.uuid().nullable().default(null),
    /**
     * The inspection item the finding is recorded on; fixed once saved. Its area, work category and
     * name are recorded on the finding (the area and component fields are then ignored).
     */
    inspectionItemId: z.uuid().nullable().default(null),
    /** A finding is closed (resolved or withdrawn), never deleted, once it is on an item. */
    status: z.enum(FINDING_STATUSES).default('open'),
    /** Why it was resolved or withdrawn (required to withdraw; empty while open). */
    resolutionNote: optionalText(HANDOVER_LIMITS.resolutionNote).default(null),
    description: requiredText(HANDOVER_LIMITS.description),
    inspectedOn: isoDate,
    /** Photos of this handover, in display order: uploaded ones are attached, missing ones removed. */
    photoIds: z.array(z.uuid()).max(HANDOVER_LIMITS.photosPerFinding),
})
    .strict();
export const meterInputSchema = z
    .object({
    id: z.uuid().nullable(),
    meterType: requiredText(HANDOVER_LIMITS.meterType),
    meterNumber: optionalText(HANDOVER_LIMITS.meterNumber),
    reading: z.string().trim().regex(READING_PATTERN).nullable(),
    note: optionalText(HANDOVER_LIMITS.note),
})
    .strict();
/**
 * `PUT /projects/:id/handovers/:handoverId`: the whole draft as the user sees it, in order. Items
 * not listed are removed; the save is atomic and only applies to the version that was edited.
 */
export const handoverSaveRequestSchema = z
    .object({
    rowVersion: z.number().int().min(1),
    handoverDate: isoDate.nullable(),
    findings: z.array(findingInputSchema).max(HANDOVER_LIMITS.findings),
    meters: z.array(meterInputSchema).max(HANDOVER_LIMITS.meters),
    /** Results of inspection items (items not listed keep theirs). */
    inspection: z
        .array(inspectionResultInputSchema)
        .max(HANDOVER_LIMITS.inspectionItems)
        .default([]),
    /** Results of general checks (checks not listed keep theirs). */
    generalChecks: z
        .array(generalCheckResultInputSchema)
        .max(HANDOVER_LIMITS.generalChecks)
        .default([]),
    /** Final handover: reinspections of initial findings (findings not listed keep theirs). */
    reinspections: z.array(reinspectionInputSchema).max(HANDOVER_LIMITS.reinspections).default([]),
})
    .strict()
    .superRefine((request, context) => {
    const seen = new Set();
    const photos = new Set();
    for (const [list, items] of [
        ['findings', request.findings],
        ['reinspections', request.reinspections],
    ]) {
        items.forEach((item, index) => {
            item.photoIds.forEach((photoId, at) => {
                if (photos.has(photoId))
                    context.addIssue({
                        code: 'custom',
                        message: 'duplicate',
                        path: [list, index, 'photoIds', at],
                    });
                photos.add(photoId);
            });
        });
    }
    const reinspected = new Set();
    request.reinspections.forEach((r, index) => {
        if (reinspected.has(r.findingId))
            context.addIssue({
                code: 'custom',
                message: 'duplicate',
                path: ['reinspections', index, 'findingId'],
            });
        reinspected.add(r.findingId);
    });
    for (const [list, items] of [
        ['findings', request.findings],
        ['meters', request.meters],
        ['inspection', request.inspection],
        ['generalChecks', request.generalChecks],
    ]) {
        items.forEach((item, index) => {
            if (item.id === null)
                return;
            if (seen.has(item.id))
                context.addIssue({ code: 'custom', message: 'duplicate', path: [list, index, 'id'] });
            seen.add(item.id);
        });
    }
});
/** `GET /projects/:id/handover-catalogs`: the entries a finding can reference (active and not). */
export const handoverCatalogsResponseSchema = z
    .object({
    areas: z.array(catalogEntrySchema),
    components: z.array(catalogEntrySchema),
    contractors: z.array(catalogEntrySchema),
    workCategories: z.array(catalogEntrySchema),
})
    .strict();
//# sourceMappingURL=handover.js.map